import { Fragment, useEffect, useState } from 'react';
import {
  Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip,
} from 'recharts';
import { transactionApi, assetApi, summaryApi, categoryApi, bankSavingApi } from '../lib/api';
import type { Asset, BankSavingContract, Category, RangeSummary, Transaction, TransactionType } from '@asset-mgmt/shared';
import { TransactionModal } from '../components/TransactionModal';
import { BankSavingContractModal } from '../components/BankSavingContractModal';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

// ── Icons ──────────────────────────────────────────────────────────────────────
function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
function ChevronLeftIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M7 8h10M11 12h2M13 16h-2" />
    </svg>
  );
}

// ── Dark mode hook ────────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// ── Period helpers ─────────────────────────────────────────────────────────────

type PeriodMode = 'weekly' | 'monthly' | 'annually' | 'custom';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekBounds(anchor: Date) {
  const d   = new Date(anchor);
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmtShort = (dt: Date) =>
    dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fmtYear = sun.getFullYear();
  const label = `${fmtShort(mon)} – ${fmtShort(sun)}, ${fmtYear}`;
  return { from: toDateStr(mon), to: toDateStr(sun), label };
}

function getMonthBounds(anchor: Date) {
  const y   = anchor.getFullYear();
  const m   = anchor.getMonth();
  const from = new Date(y, m, 1);
  const to   = new Date(y, m + 1, 0);
  const label = anchor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  return { from: toDateStr(from), to: toDateStr(to), label };
}

function getYearBounds(anchor: Date) {
  const y = anchor.getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31`, label: String(y) };
}

function getPeriodBounds(
  mode: PeriodMode, anchor: Date, customFrom: string, customTo: string,
) {
  if (mode === 'weekly')   return getWeekBounds(anchor);
  if (mode === 'monthly')  return getMonthBounds(anchor);
  if (mode === 'annually') return getYearBounds(anchor);
  const label = customFrom && customTo
    ? `${customFrom} – ${customTo}`
    : 'Pick a range';
  return { from: customFrom, to: customTo, label };
}

function shiftAnchor(anchor: Date, mode: PeriodMode, dir: 1 | -1): Date {
  const d = new Date(anchor);
  if (mode === 'weekly')   d.setDate(d.getDate() + dir * 7);
  if (mode === 'monthly')  d.setMonth(d.getMonth() + dir);
  if (mode === 'annually') d.setFullYear(d.getFullYear() + dir);
  return d;
}

// ── Formatting ─────────────────────────────────────────────────────────────────

function fmtDate(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function fmtAmount(amount: number) {
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function groupByDate<T extends { date: string }>(txns: T[]): Array<{ date: string; items: T[] }> {
  const groups: Array<{ date: string; items: T[] }> = [];
  for (const t of txns) {
    const key = t.date.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last?.date === key) last.items.push(t);
    else groups.push({ date: key, items: [t] });
  }
  return groups;
}

function fmtDateGroup(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Transactions() {
  const { user } = useAuth();
  const toast = useToast();
  const baseCurrency = user?.settings.baseCurrency ?? 'USD';
  const isViewer = user?.role === 'viewer';

  const now = new Date();
  const [mode,       setMode]       = useState<PeriodMode>('monthly');
  const [anchor,     setAnchor]     = useState<Date>(now);
  const [customFrom, setCustomFrom] = useState(toDateStr(now));
  const [customTo,   setCustomTo]   = useState(toDateStr(now));

  const [txns,    setTxns]    = useState<Transaction[]>([]);
  const [assets,  setAssets]  = useState<Asset[]>([]);
  const [rangeSummary, setRangeSummary] = useState<RangeSummary | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [editTxn,  setEditTxn]  = useState<Transaction | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bankSavingModal, setBankSavingModal] = useState<{
    asset: Asset;
    initialMode?: 'open' | 'close' | 'withdraw';
    initialContract?: BankSavingContract;
  } | null>(null);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState<TransactionType | 'all'>('all');
  const [filterAsset, setFilterAsset] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [categories,  setCategories]  = useState<Category[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCharts,  setShowCharts]  = useState(false);

  const { from, to, label: periodLabel } = getPeriodBounds(mode, anchor, customFrom, customTo);

  function reload() {
    if (!from || !to) return;
    setLoading(true);
    setSelectedIds(new Set());
    Promise.all([
      transactionApi.listRange(from, to),
      assetApi.list(),
      summaryApi.range(from, to),
    ])
      .then(([t, a, s]) => {
        setTxns(t);
        setAssets(a);
        setRangeSummary(s);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [from, to]);

  useEffect(() => {
    categoryApi.list().then(setCategories).catch(() => {});
  }, []);

  async function handleDelete(id: string) {
    if (!confirm('Delete this transaction?')) return;
    try {
      await transactionApi.remove(id);
      toast.success('Transaction deleted.');
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete transaction.');
    }
  }

  async function handleBatchDelete() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected transaction${selectedIds.size > 1 ? 's' : ''}?`)) return;
    try {
      await Promise.all([...selectedIds].map((id) => transactionApi.remove(id)));
      toast.success(`${selectedIds.size} transaction${selectedIds.size > 1 ? 's' : ''} deleted.`);
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete transactions.');
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) =>
      prev.size === displayed.length ? new Set() : new Set(displayed.map((t) => t.id)),
    );
  }

  function openEdit(txn: Transaction) { setEditTxn(txn); setShowForm(true); }
  function openNew() {
    const selectedAsset = assets.find((a) => a.id === filterAsset);
    if (selectedAsset?.type === 'bank_saving') {
      setBankSavingModal({ asset: selectedAsset, initialMode: 'open' });
    } else {
      setEditTxn(null);
      setShowForm(true);
    }
  }
  function closeForm()                 { setShowForm(false); setEditTxn(null); setSelectedIds(new Set()); reload(); }

  const assetMap = Object.fromEntries(assets.map((a) => [a.id, a.name]));
  const catById  = new Map(categories.map((cat) => [cat.id, cat]));
  const sorted   = [...txns].sort((a, b) => b.date.localeCompare(a.date));

  // Apply client-side filters
  const q = search.trim().toLowerCase();
  const displayed = sorted.filter((t) => {
    if (filterType !== 'all' && t.type !== filterType) return false;
    if (filterAsset !== 'all' && t.assetId !== filterAsset) return false;
    if (filterCategory !== 'all' && t.categoryId !== filterCategory) return false;
    if (q) {
      const fields = [
        assetMap[t.assetId] ?? '',
        catById.get(t.categoryId)?.name ?? '',
        t.note ?? '',
        t.type,
        t.currency,
        String(t.amount),
        t.date,
      ].map((f) => f.toLowerCase());
      if (!fields.some((f) => f.includes(q))) return false;
    }
    return true;
  });

  const activeFilters =
    (filterType !== 'all' ? 1 : 0) +
    (filterAsset !== 'all' ? 1 : 0) +
    (filterCategory !== 'all' ? 1 : 0) +
    (q ? 1 : 0);

  function clearFilters() {
    setSearch('');
    setFilterType('all');
    setFilterAsset('all');
    setFilterCategory('all');
  }

  const totalIncome  = rangeSummary?.income  ?? 0;
  const totalExpense = rangeSummary?.expense ?? 0;
  const net          = totalIncome - totalExpense;

  // Derive pie data from filtered transactions grouped by category
  function groupByCategory(type: TransactionType) {
    return Object.entries(
      displayed
        .filter((t) => t.type === type)
        .reduce<Record<string, number>>((acc, t) => {
          acc[t.categoryId] = (acc[t.categoryId] ?? 0) + t.amount;
          return acc;
        }, {})
    )
      .map(([categoryId, value]) => {
        const cat = catById.get(categoryId);
        return {
          name:  cat ? `${cat.icon ? `${cat.icon} ` : ''}${cat.name}` : 'Uncategorized',
          color: cat?.color ?? '#9ca3af',
          value: value as number,
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }

  const expensePie = groupByCategory('expense');
  const incomePie  = groupByCategory('income');

  const dark = useDarkMode();
  const tooltipStyle: { [key: string]: string | number } = {
    background:   dark ? '#1f2937' : '#ffffff',
    border:       `1px solid ${dark ? '#374151' : '#e5e7eb'}`,
    borderRadius: 8,
    fontSize:     12,
    color:        dark ? '#f3f4f6' : '#111827',
  };

  const expenseTotal = expensePie.reduce((s, d) => s + d.value, 0);
  const incomeTotal  = incomePie.reduce((s, d) => s + d.value, 0);

  function makePieTooltip(total: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function PieTooltip({ active, payload }: any) {
      if (!active || !payload?.length) return null;
      const { name, value, payload: { fill } } = payload[0];
      const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
      return (
        <div style={tooltipStyle} className="px-3 py-2 shadow-lg">
          <div className="flex items-center gap-2 mb-1">
            <span style={{ background: fill }} className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" />
            <span className="font-medium text-xs leading-snug max-w-[160px] break-words">{name}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 text-xs">
            <span className="text-gray-400 dark:text-gray-500">Amount</span>
            <span className="font-mono font-semibold">{fmtAmount(value)} {baseCurrency}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 text-xs mt-0.5">
            <span className="text-gray-400 dark:text-gray-500">Share</span>
            <span className="font-mono font-semibold">{pct}%</span>
          </div>
        </div>
      );
    };
  }

  const MODE_LABELS: Record<PeriodMode, string> = {
    weekly: 'Weekly', monthly: 'Monthly', annually: 'Annually', custom: 'Custom',
  };

  const groupedDisplayed = groupByDate(displayed);

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Transactions</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {periodLabel}
            {!loading && (
              <>
                {' '}·{' '}
                <span className="font-medium">
                  {activeFilters > 0 ? `${displayed.length} of ${sorted.length}` : sorted.length}
                </span>{' '}
                record{sorted.length !== 1 ? 's' : ''}
              </>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Period mode — select on mobile, tabs on sm+ */}
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as PeriodMode)}
            className="sm:hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm outline-none"
          >
            {(Object.keys(MODE_LABELS) as PeriodMode[]).map((m) => (
              <option key={m} value={m}>{MODE_LABELS[m]}</option>
            ))}
          </select>
          <div className="hidden sm:flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
            {(Object.keys(MODE_LABELS) as PeriodMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === m
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {MODE_LABELS[m]}
              </button>
            ))}
          </div>

          {/* Navigator (not shown for custom) */}
          {mode !== 'custom' && (
            <div className="flex items-center gap-0.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
              <button
                onClick={() => setAnchor((a) => shiftAnchor(a, mode, -1))}
                className="rounded-l-xl p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronLeftIcon />
              </button>
              <span className="min-w-[100px] sm:min-w-[120px] px-1 sm:px-2 text-center text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200">
                {periodLabel}
              </span>
              <button
                onClick={() => setAnchor((a) => shiftAnchor(a, mode, 1))}
                className="rounded-r-xl p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronRightIcon />
              </button>
            </div>
          )}

          {/* Custom date range inputs */}
          {mode === 'custom' && (
            <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 shadow-sm">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none dark:[color-scheme:dark]"
              />
              <span className="text-gray-400 dark:text-gray-600">–</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none dark:[color-scheme:dark]"
              />
            </div>
          )}

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`relative flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors ${
              showFilters || activeFilters > 0
                ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            <FilterIcon />
            <span className="hidden sm:inline">Filters</span>
            {activeFilters > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
                {activeFilters}
              </span>
            )}
          </button>

          <button
            onClick={openNew}
            disabled={isViewer}
            title={isViewer ? 'Viewers cannot add transactions' : undefined}
            className="hidden items-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            <PlusIcon />
            Add Transaction
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      {showFilters && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm">
          {/* Search */}
          <datalist id="txn-note-suggestions">
            {Array.from(new Set(txns.map((t) => t.note).filter(Boolean))).map((note) => (
              <option key={note} value={note!} />
            ))}
          </datalist>
          <div className="flex min-w-[180px] flex-1 items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
            <SearchIcon />
            <input
              type="text"
              list="txn-note-suggestions"
              placeholder="Search note or asset…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <CloseIcon />
              </button>
            )}
          </div>

          {/* Type filter */}
          <div className="flex items-center gap-1">
            {(['all', 'income', 'expense', 'transfer'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  filterType === t
                    ? t === 'income'   ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                    : t === 'expense'  ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                    : t === 'transfer' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t === 'all' ? 'All types' : t}
              </button>
            ))}
          </div>

          {/* Asset filter */}
          <select
            value={filterAsset}
            onChange={(e) => setFilterAsset(e.target.value)}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 outline-none"
          >
            <option value="all">All assets</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>

          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 outline-none"
          >
            <option value="all">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.icon ? `${cat.icon} ` : ''}{cat.name}</option>
            ))}
          </select>

          {/* Clear */}
          {activeFilters > 0 && (
            <button
              onClick={clearFilters}
              className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* ── Summary cards ── */}
      {!loading && txns.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <SummaryCard label="Income"   value={totalIncome}  currency={baseCurrency} variant="income"   />
          <SummaryCard label="Expenses" value={totalExpense} currency={baseCurrency} variant="expense"  />
          <SummaryCard label="Net"      value={net}          currency={baseCurrency} variant={net >= 0 ? 'income' : 'expense'} />
        </div>
      )}

      {/* ── Pie charts ── */}
      {!loading && displayed.length > 0 && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          {/* Toggle header */}
          <button
            onClick={() => setShowCharts((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors rounded-2xl"
          >
            <span className="flex items-center gap-2">
              <span>📊</span> Breakdown by Category
              {activeFilters > 0 && (
                <span className="rounded-full bg-primary-100 dark:bg-primary-900/40 px-2 py-0.5 text-xs font-medium text-primary-700 dark:text-primary-400">
                  filtered
                </span>
              )}
            </span>
            <svg
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showCharts ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCharts && (
            <div className="grid grid-cols-1 gap-4 px-4 pb-4 sm:grid-cols-2">
              {/* Expense by note */}
              <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 p-3">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Expenses by Category</h3>
                {expensePie.length === 0 ? (
                  <p className="flex h-[180px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">No expense data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={expensePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={78} paddingAngle={2}>
                        {expensePie.map((entry, i) => (
                          <Cell key={i} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip content={makePieTooltip(expenseTotal)} />
                      <Legend wrapperStyle={{ fontSize: 11, color: dark ? '#9ca3af' : '#6b7280' }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Income by note */}
              <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 p-3">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Income by Category</h3>
                {incomePie.length === 0 ? (
                  <p className="flex h-[180px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">No income data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={incomePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={78} paddingAngle={2}>
                        {incomePie.map((entry, i) => (
                          <Cell key={i} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip content={makePieTooltip(incomeTotal)} />
                      <Legend wrapperStyle={{ fontSize: 11, color: dark ? '#9ca3af' : '#6b7280' }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-600 border-t-transparent" />
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState periodLabel={periodLabel} onAdd={openNew} isViewer={isViewer} />
      ) : displayed.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-16 text-center">
          <p className="text-4xl">🔍</p>
          <p className="mt-3 font-semibold text-gray-700 dark:text-gray-300">No matches</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your filters.</p>
          <button onClick={clearFilters} className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Clear filters</button>
        </div>
      ) : (
        <>
          {/* Selection action bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between rounded-xl border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 px-4 py-2.5 shadow-sm">
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                {selectedIds.size} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => void handleBatchDelete()}
                  disabled={isViewer}
                  className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <TrashIcon />
                  Delete {selectedIds.size}
                </button>
              </div>
            </div>
          )}

          {/* Desktop table */}
          <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={displayed.length > 0 && selectedIds.size === displayed.length}
                      ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < displayed.length; }}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                    />
                  </th>
                  {['Date', 'Asset', 'Note', 'Type', 'Amount', ''].map((h) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 ${h === 'Amount' ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedDisplayed.map(({ date, items }) => (
                  <Fragment key={date}>
                    <tr className="bg-gray-50/60 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
                      <td colSpan={7} className="px-4 py-1.5">
                        <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                          {fmtDateGroup(date)}
                        </span>
                      </td>
                    </tr>
                    {items.map((t) => (
                      <tr key={t.id} className={`group border-b border-gray-50 dark:border-gray-700/50 transition-colors hover:bg-primary-50/40 dark:hover:bg-gray-700/30 ${selectedIds.has(t.id) ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''}`}>
                        <td className="px-4 py-3.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(t.id)}
                            onChange={() => toggleSelect(t.id)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3.5 text-gray-500 dark:text-gray-400">
                          {fmtDate(t.date)}
                        </td>
                        <td className="max-w-[160px] truncate px-4 py-3.5 font-medium text-gray-800 dark:text-gray-200">
                          {assetMap[t.assetId] ?? t.assetId}
                        </td>
                        <td className="max-w-xs px-4 py-3.5 text-gray-500 dark:text-gray-400">
                          {(() => {
                            const cat = catById.get(t.categoryId);
                            if (!cat) return null;
                            return (
                              <span className="mb-0.5 inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                                <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: cat.color }} />
                                {cat.icon && <span>{cat.icon}</span>}
                                <span>{cat.name}</span>
                              </span>
                            );
                          })()}
                          <div className="truncate">{t.note ?? <span className="text-gray-300 dark:text-gray-600">—</span>}</div>
                          {t.description && (
                            <div className="mt-0.5 truncate text-xs text-gray-400 dark:text-gray-500">{t.description}</div>
                          )}
                          {t.createdByName && (
                            <div className="mt-0.5 text-xs text-violet-500 dark:text-violet-400">by {t.createdByName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <TypeBadge type={t.type} />
                        </td>
                        <td className={`px-4 py-3.5 text-right font-mono font-semibold tabular-nums ${
                          t.type === 'income'  ? 'text-emerald-600 dark:text-emerald-400'
                        : t.type === 'expense' ? 'text-red-500 dark:text-red-400'
                        : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {t.type === 'income' ? '+' : t.type === 'expense' ? '−' : ''}
                          {fmtAmount(t.amount)}{' '}
                          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">{t.currency}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              onClick={() => openEdit(t)}
                              disabled={isViewer}
                              title={isViewer ? 'Viewers cannot edit transactions' : 'Edit transaction'}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <EditIcon />
                            </button>
                            <button
                              onClick={() => void handleDelete(t.id)}
                              disabled={isViewer}
                              title={isViewer ? 'Viewers cannot delete transactions' : 'Delete transaction'}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-4">
            {groupedDisplayed.map(({ date, items }) => (
              <div key={date}>
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {fmtDateGroup(date)}
                </p>
                <div className="space-y-2">
                  {items.map((t) => (
                    <div
                      key={t.id}
                      className={`rounded-xl border bg-white dark:bg-gray-800 px-4 py-3 shadow-sm transition-colors ${
                        selectedIds.has(t.id)
                          ? 'border-primary-300 dark:border-primary-700 bg-primary-50/60 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(t.id)}
                            onChange={() => toggleSelect(t.id)}
                            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                          />
                          <div className="min-w-0 flex-1">
                            <TypeBadge type={t.type} />
                            <p className="mt-1 truncate font-medium text-gray-800 dark:text-gray-200">
                              {assetMap[t.assetId] ?? t.assetId}
                            </p>
                            {(() => {
                              const cat = catById.get(t.categoryId);
                              if (!cat) return null;
                              return (
                                <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                                  <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: cat.color }} />
                                  {cat.icon && <span>{cat.icon}</span>}
                                  <span>{cat.name}</span>
                                </span>
                              );
                            })()}
                            {t.note && (
                              <p className="truncate text-sm text-gray-500 dark:text-gray-400">{t.note}</p>
                            )}
                            {t.description && (
                              <p className="truncate text-xs text-gray-400 dark:text-gray-500 mt-0.5">{t.description}</p>
                            )}
                            {t.createdByName && (
                              <p className="text-xs text-violet-500 dark:text-violet-400 mt-0.5">by {t.createdByName}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 flex-col items-end gap-2">
                          <span className={`font-mono font-semibold tabular-nums ${
                            t.type === 'income'  ? 'text-emerald-600 dark:text-emerald-400'
                          : t.type === 'expense' ? 'text-red-500 dark:text-red-400'
                          : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {t.type === 'income' ? '+' : t.type === 'expense' ? '−' : ''}
                            {fmtAmount(t.amount)} {t.currency}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEdit(t)}
                              disabled={isViewer}
                              title={isViewer ? 'Viewers cannot edit transactions' : undefined}
                              className="rounded-lg px-2 py-1 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => void handleDelete(t.id)}
                              disabled={isViewer}
                              title={isViewer ? 'Viewers cannot delete transactions' : undefined}
                              className="rounded-lg px-2 py-1 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showForm && (
        <TransactionModal
          assets={assets}
          initialData={editTxn}
          onClose={closeForm}
          onBankSavingSelected={(asset) => {
            setShowForm(false);
            setEditTxn(null);
            setBankSavingModal({ asset, initialMode: 'open' });
          }}
        />
      )}

      {bankSavingModal !== null && (
        <BankSavingContractModal
          asset={bankSavingModal.asset}
          bankAssets={assets.filter((a) => a.type === 'bank_account')}
          {...(bankSavingModal.initialMode !== undefined && { initialMode: bankSavingModal.initialMode })}
          {...(bankSavingModal.initialContract !== undefined && { initialContract: bankSavingModal.initialContract })}
          onClose={() => setBankSavingModal(null)}
          onSuccess={() => { setBankSavingModal(null); reload(); }}
        />
      )}

      {/* FAB — always visible, above bottom tabs on mobile */}
      <button
        onClick={openNew}
        disabled={isViewer}
        title={isViewer ? 'Viewers cannot add transactions' : undefined}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg ring-4 ring-primary-600/20 transition-transform hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
        aria-label="Add transaction"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
}

// ── SummaryCard ────────────────────────────────────────────────────────────────
function SummaryCard({
  label, value, currency, variant,
}: {
  label: string;
  value: number;
  currency: string;
  variant: 'income' | 'expense';
}) {
  const colors = {
    income:  'text-emerald-600 dark:text-emerald-400',
    expense: 'text-red-500 dark:text-red-400',
  };
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 sm:p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 sm:mt-1.5 truncate text-base sm:text-lg font-bold tabular-nums ${colors[variant]}`}>
        {fmtAmount(value)}{' '}
        <span className="text-xs font-normal text-gray-400 dark:text-gray-500">{currency}</span>
      </p>
    </div>
  );
}

// ── EmptyState ─────────────────────────────────────────────────────────────────
function EmptyState({ periodLabel, onAdd, isViewer }: { periodLabel: string; onAdd: () => void; isViewer: boolean }) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-20 text-center">
      <p className="text-5xl">💳</p>
      <p className="mt-3 font-semibold text-gray-700 dark:text-gray-300">No transactions</p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Nothing recorded for {periodLabel} yet.
      </p>
      {isViewer ? (
        <p className="mt-4 text-xs text-amber-600 dark:text-amber-400">You have view-only access.</p>
      ) : (
        <button
          onClick={onAdd}
          className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
        >
          <PlusIcon /> Add Transaction
        </button>
      )}
    </div>
  );
}

// ── TypeBadge ──────────────────────────────────────────────────────────────────
function TypeBadge({ type }: { type: TransactionType }) {
  const cls: Record<TransactionType, string> = {
    income:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
    expense:  'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400',
    transfer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  };
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${cls[type]}`}>
      {type}
    </span>
  );
}
